// Comprehensive lanelet validation in one place
// Uses existing heavy geometry checks, inlines simple property checks for performance.

import * as Cesium from "cesium";
import { getInvalidLaneletsWithReasons } from "./Full"; // from Full.js
import { getEntitiesWithNegativeHeights } from "../NegativeAlt"; // from NegativeAlt.js
import { PROPERTY_STANDARDS } from "../InvalidValue"; // from InvalidValue.js

// Only these keys are considered for enum/empty/missing checks
const LANELET_KEYS = ["subtype", "turn_direction", "one_way", "speed_limit", "right_line_type", "left_line_type"];

// Normalize string for enum comparison
function normalizeValue(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim().toLowerCase();
}

// Check if a value is considered "empty"
function isEmptyValue(value) {
  if (value === null || value === undefined) return true;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return true;
    const lower = trimmed.toLowerCase();
    if (lower === "null" || lower === "undefined") return true;
    return false;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  return false;
}

// Read property value and also whether key exists
function getPropertyValueAndExists(props, key, time) {
  if (!props) return { exists: false, value: undefined };

  // Cesium PropertyBag with get()
  if (typeof props.get === "function") {
    const property = props.get(key);
    if (property !== undefined) {
      let v;
      if (property && typeof property.getValue === "function") {
        v = property.getValue(time);
      } else {
        v = property;
      }
      return { exists: true, value: v };
    }
  }

  // Internal _properties
  if (props._properties && typeof props._properties === "object") {
    if (Object.prototype.hasOwnProperty.call(props._properties, key)) {
      const property = props._properties[key];
      let v;
      if (property && typeof property.getValue === "function") {
        v = property.getValue(time);
      } else {
        v = property;
      }
      return { exists: true, value: v };
    }
  }

  // PropertyBag with getValue() returning object
  if (typeof props.getValue === "function") {
    const obj = props.getValue(time);
    if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, key)) {
      return { exists: true, value: obj[key] };
    }
  }

  // Plain object
  if (Object.prototype.hasOwnProperty.call(props, key)) {
    return { exists: true, value: props[key] };
  }

  return { exists: false, value: undefined };
}

// Get properties.id or fall back to entity.id
function getEntityId(entity) {
  if (!entity) return undefined;
  const props = entity.properties;
  let idFromProps;

  if (props) {
    if (typeof props.get === "function") {
      const property = props.get("id");
      if (property) {
        if (typeof property.getValue === "function") {
          idFromProps = property.getValue();
        } else {
          idFromProps = property;
        }
      }
    }

    if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
      const property = props._properties.id;
      if (property) {
        if (typeof property.getValue === "function") {
          idFromProps = property.getValue();
        } else {
          idFromProps = property;
        }
      }
    }

    if (idFromProps === undefined && typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
        idFromProps = obj.id;
      }
    }

    if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
      idFromProps = props.id;
    }
  }

  if (idFromProps !== undefined && idFromProps !== null) {
    return idFromProps;
  }

  return entity.id;
}

// Main combined validator
// Returns: [{ entity, id, reasons: string }] to feed into setValidationResults
export function getComprehensiveLaneletIssues(viewer, currentDataSource) {
  if (!currentDataSource || !currentDataSource.entities) return [];

  const entities = currentDataSource.entities.values || currentDataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  const time = (viewer && viewer.clock && viewer.clock.currentTime) || Cesium.JulianDate.now();

  // id / entity dedupe and reasons aggregation
  const byKey = new Map(); // key = id ?? entity

  function ensureEntry(entity, id) {
    const key = id ?? entity;
    let entry = byKey.get(key);
    if (!entry) {
      entry = { entity, id, reasons: [] };
      byKey.set(key, entry);
    }
    return entry;
  }

  function addReason(entity, id, reason) {
    if (!reason) return;
    const entry = ensureEntry(entity, id);
    entry.reasons.push(reason);
  }

  // 1) Full topology / distance / type validator (heavy, already optimized)
  const laneletIssues = getInvalidLaneletsWithReasons(viewer, currentDataSource) || [];
  for (const item of laneletIssues) {
    const id = item.id ?? getEntityId(item.entity);
    if (!item.reasons) continue;
    const rs = Array.isArray(item.reasons) ? item.reasons : [item.reasons];
    for (const r of rs) {
      addReason(item.entity, id, r);
    }
  }

  // 2) Negative heights check (geometry scan)
  const negativeIssues = getEntitiesWithNegativeHeights(currentDataSource) || [];
  for (const item of negativeIssues) {
    const id = item.id ?? getEntityId(item.entity);
    // item.reasons is already a short English summary
    addReason(item.entity, id, "Has points with negative height");
  }

  // 3) Single pass over entities for enum / empty / missing on selected keys
  for (const entity of entities) {
    if (!entity || !entity.properties) continue;
    const props = entity.properties;
    const id = getEntityId(entity);
    let hasAnyIssue = false;
    const localReasons = [];

    for (const key of LANELET_KEYS) {
      const { exists, value } = getPropertyValueAndExists(props, key, time);

      // Missing key
      if (!exists) {
        localReasons.push(`Missing key: ${key}`);
        hasAnyIssue = true;
        continue;
      }

      // Empty value
      if (isEmptyValue(value)) {
        localReasons.push(`Empty value for key: ${key}`);
        hasAnyIssue = true;
        continue;
      }

      // Enum validation
      const allowed = PROPERTY_STANDARDS && PROPERTY_STANDARDS[key];
      if (Array.isArray(allowed) && allowed.length > 0) {
        const allowedNorm = allowed.map(normalizeValue);

        const checkOne = (v) => {
          const norm = normalizeValue(v);
          if (!norm) return true; // empty already handled
          if (!allowedNorm.includes(norm)) {
            localReasons.push(`Invalid value for ${key}: "${v}"`);
            return false;
          }
          return true;
        };

        if (Array.isArray(value)) {
          for (const v of value) {
            checkOne(v);
          }
        } else {
          checkOne(value);
        }

        if (localReasons.length > 0) {
          hasAnyIssue = true;
        }
      }
    }

    if (hasAnyIssue && localReasons.length > 0) {
      for (const r of localReasons) {
        addReason(entity, id, r);
      }
    }
  }

  // Build final list in the same shape your UI expects
  const result = [];
  for (const entry of byKey.values()) {
    if (!entry.reasons || entry.reasons.length === 0) continue;
    // Keep reasons as array
    result.push({
      entity: entry.entity,
      id: entry.id,
      reasons: entry.reasons, // <-- array of strings
    });
  }

  return result;
}
